import axios from "axios";
import { APP_PATH } from "../api/backendPath";

// Create axios instance with auth token
const createAxiosInstance = () => {
  const token = sessionStorage.getItem("usr_1a2b3c");
  return axios.create({
    baseURL: process.env.REACT_APP_API_URL,
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
};

/**
 * Get all lab technicians
 * @param {Object} params - Query parameters for filtering
 * @returns {Promise} Response from the API
 */
export const getLabTechnicians = async (params = {}) => {
  try {
    const axiosInstance = createAxiosInstance();
    const queryParams = new URLSearchParams();

    if (params.page) queryParams.append("page", params.page);
    if (params.limit) queryParams.append("limit", params.limit);
    if (params.search) queryParams.append("search", params.search);
    if (params.gender) queryParams.append("gender", params.gender);
    if (params.status) queryParams.append("status", params.status);

    const response = await axiosInstance.get(
      `${APP_PATH.labTechnicians}?${queryParams.toString()}`,
    );
    return response.data;
  } catch (error) {
    console.error("Error fetching lab technicians:", error);
    throw error;
  }
};

/**
 * Create a new lab technician
 * @param {Object} technicianData - Technician data object
 * @returns {Promise} Response from the API
 */
export const createLabTechnician = async (technicianData) => {
  try {
    const axiosInstance = createAxiosInstance();
    const response = await axiosInstance.post(
      APP_PATH.labTechnicians,
      technicianData,
    );

    // Check if the response indicates an error even with 201 status
    if (response.data && response.data.status === false) {
      const error = new Error(
        response.data.message || "Failed to create lab technician",
      );
      error.response = { data: response.data };
      throw error;
    }

    return response.data;
  } catch (error) {
    console.error("Error creating lab technician:", error);
    throw error;
  }
};

/**
 * Technician genders this lab may register, as assigned by the admin
 * ("Gender Preference" on the lab user → labs.allows_male_staff / allows_female_staff,
 * returned by GET /lab/profile).
 *
 * A missing flag counts as permitted, matching the backend column default, so labs
 * created before the setting existed stay unrestricted. If the profile can't be loaded
 * both genders are offered; the backend still rejects a gender that isn't permitted.
 * @returns {Promise<string[]>} Subset of ["Male", "Female"], never empty
 */
export const getPermittedTechnicianGenders = async () => {
  try {
    const axiosInstance = createAxiosInstance();
    const response = await axiosInstance.get(APP_PATH.labProfile);
    const lab = response.data?.data || {};
    const genders = [];
    if (lab.allows_male_staff !== false) genders.push("Male");
    if (lab.allows_female_staff !== false) genders.push("Female");
    return genders.length ? genders : ["Male", "Female"];
  } catch (error) {
    console.error("Error fetching permitted technician genders:", error);
    return ["Male", "Female"];
  }
};

/**
 * Get a single technician by ID
 * @param {string} technicianId - The technician ID
 * @returns {Promise} Response from the API
 */
export const getLabTechnicianById = async (technicianId) => {
  try {
    const axiosInstance = createAxiosInstance();
    const response = await axiosInstance.get(
      `${APP_PATH.labTechnicians}/${technicianId}`,
    );
    return response.data;
  } catch (error) {
    console.error("Error fetching lab technician:", error);
    throw error;
  }
};

/**
 * Update an existing lab technician
 * @param {string} technicianId - The technician ID
 * @param {Object} technicianData - Updated technician data
 * @returns {Promise} Response from the API
 */
export const updateLabTechnician = async (technicianId, technicianData) => {
  try {
    const axiosInstance = createAxiosInstance();
    const response = await axiosInstance.put(
      `${APP_PATH.labTechnicians}/${technicianId}`,
      technicianData,
    );

    // Check if the response indicates an error
    if (response.data && response.data.status === false) {
      const error = new Error(
        response.data.message || "Failed to update lab technician",
      );
      error.response = { data: response.data };
      throw error;
    }

    return response.data;
  } catch (error) {
    console.error("Error updating lab technician:", error);
    throw error;
  }
};

/**
 * Delete a lab technician
 * @param {string} technicianId - The technician ID
 * @returns {Promise} Response from the API
 */
export const deleteLabTechnician = async (technicianId) => {
  try {
    const axiosInstance = createAxiosInstance();
    const response = await axiosInstance.delete(
      `${APP_PATH.labTechnicians}/${technicianId}`,
    );

    // The gateway reports failures (e.g. "Cannot deactivate technician with
    // active assignments") as `status: false` in the body, not as an HTTP error.
    if (response.data && response.data.status === false) {
      const error = new Error(
        response.data.message || "Failed to delete lab technician",
      );
      error.response = { data: response.data };
      throw error;
    }

    return response.data;
  } catch (error) {
    console.error("Error deleting lab technician:", error);
    throw error;
  }
};

/**
 * Get technician assignments
 * @param {string} technicianId - The technician ID
 * @param {Object} params - Query parameters for filtering
 * @returns {Promise} Response from the API
 */
export const getTechnicianAssignments = async (technicianId, params = {}) => {
  try {
    const axiosInstance = createAxiosInstance();
    const queryParams = new URLSearchParams();

    if (params.date_from) queryParams.append("date_from", params.date_from);
    if (params.date_to) queryParams.append("date_to", params.date_to);
    if (params.page) queryParams.append("page", params.page);
    if (params.limit) queryParams.append("limit", params.limit);

    const response = await axiosInstance.get(
      `${APP_PATH.labTechnicians}/${technicianId}/assignments?${queryParams.toString()}`,
    );
    return response.data;
  } catch (error) {
    console.error("Error fetching technician assignments:", error);
    throw error;
  }
};
